import React, { useCallback, useEffect, useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import {
  SingleDropdown,
  SimpleSingleDropdown,
  MultiDropdown,
  useSingleDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  standardRequestWithErrorFlash,
  standardRequestWithFlash,
} from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import {
  toMultiDropdownOptions,
  toNestedDropdownOptions,
  type Option,
} from 'shared/utils/formUtils';

const t = withLocalePrefix('talent_matching.rediscovery.table.prospect_modal');

type ProspectPool = {
  id: number;
  name: string;
  prospect_pool_stages: Array<{ id: number; name: string }>;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  makeProspectPath: string;
  fullName: string;
  jobsPath: string;
  departmentsPath: string;
  officesPath: string;
  prospectPoolsPath: string;
  prospectOwnerOptionsPath: string;
  sourceOptionsPath: string;
  referrerOptionsPath: string;
  defaultJob: Item;
  applicationId: string;
  onSuccess?: (metadata: Record<string, unknown>) => void;
};

const toOptions = (results: Option[], nested = false) => {
  return nested
    ? toNestedDropdownOptions(results)
    : toMultiDropdownOptions(results);
};

const extractItems = (data: Record<string, unknown>): Option[] => {
  if (data.results) {
    return data.results as Option[];
  }
  if (data.items) {
    return data.items as Option[];
  }
  if (data.data) {
    return data.data as Option[];
  }
  return data as unknown as Option[];
};

const CACHE_STALE_TIME = 1000 * 60 * 5; // 5 minutes

const useFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>,
  onRawData?: (rawItems: Option[]) => void
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (term: string): Promise<{ items: Item[] }> => {
      const queryKey = ['prospect-modal', path, term, params];

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return { items: toOptions(rawItems), rawItems };
        },
        staleTime: CACHE_STALE_TIME,
      });

      onRawData?.(result.rawItems);
      return { items: result.items };
    },
    [path, params, onRawData, queryClient]
  );
};

const usePaginatedFetchItems = (
  path: string,
  params?: Record<string, string | number | boolean>
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      term: string,
      nextPage: number
    ): Promise<{ items: Item[]; more: boolean }> => {
      const nested = Boolean(params?.nested);
      const queryKey = ['prospect-modal', path, term, nextPage, params];

      const cached = queryClient.getQueryData<{ items: Item[]; more: boolean }>(
        queryKey
      );
      if (cached) {
        return cached;
      }

      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(path, {
            params: { term, page: nextPage || 1, ...params },
          });
          const rawItems = extractItems(response.data);
          return {
            items: toOptions(rawItems, nested),
            more: response.data.more ?? false,
          };
        },
        staleTime: CACHE_STALE_TIME,
      });

      return result;
    },
    [path, params, queryClient]
  );
};

const MakeProspectModal = ({
  isOpen,
  closeModal,
  makeProspectPath,
  fullName,
  jobsPath,
  departmentsPath,
  officesPath,
  prospectPoolsPath,
  prospectOwnerOptionsPath,
  sourceOptionsPath,
  referrerOptionsPath,
  defaultJob,
  applicationId,
  onSuccess,
}: Props) => {
  const [submitting, setSubmitting] = useState(false);

  const [selectedJobs, setSelectedJobs] = useState<Item[]>([defaultJob]);
  const [selectedDepartment, setSelectedDepartment] = useState<Item | null>(
    null
  );
  const [selectedOffice, setSelectedOffice] = useState<Item | null>(null);
  const [selectedPool, setSelectedPool] = useState<Item | null>(null);
  const [selectedStage, setSelectedStage] = useState<Item | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<Item | null>(null);
  const [selectedSource, setSelectedSource] = useState<Item | null>(null);
  const [selectedReferrer, setSelectedReferrer] = useState<Item | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedJobs([defaultJob]);
      setSelectedDepartment(null);
      setSelectedOffice(null);
      setSelectedPool(null);
      setSelectedStage(null);
      setSelectedOwner(null);
      setSelectedSource(null);
      setSelectedReferrer(null);
    }
  }, [isOpen, defaultJob]);

  useEffect(() => {
    setSelectedOffice(null);
  }, [selectedDepartment]);

  useEffect(() => {
    setSelectedStage(null);
  }, [selectedPool]);

  // Remount key for office dropdown when department changes
  const [officeKey, setOfficeKey] = useState(0);
  useEffect(() => {
    setOfficeKey((k) => k + 1);
  }, [selectedDepartment]);

  const fetchJobs = usePaginatedFetchItems(jobsPath, { nested: true });
  const fetchDepartments = useFetchItems(departmentsPath);
  const fetchOffices = usePaginatedFetchItems(officesPath, {
    ...(selectedDepartment ? { department_id: selectedDepartment.value } : {}),
  });
  const [stagesByPoolId, setStagesByPoolId] = useState<Record<string, Item[]>>(
    {}
  );

  const storePoolStages = useCallback((rawItems: Option[]) => {
    const pools = rawItems as unknown as ProspectPool[];
    const map: Record<string, Item[]> = {};
    pools.forEach((pool) => {
      map[pool.id.toString()] = (pool.prospect_pool_stages || []).map(
        (stage) => ({
          value: stage.id.toString(),
          label: stage.name,
        })
      );
    });
    setStagesByPoolId(map);
  }, []);

  const fetchPools = useFetchItems(
    prospectPoolsPath,
    undefined,
    storePoolStages
  );

  const stageOptions = selectedPool
    ? stagesByPoolId[selectedPool.value] || []
    : [];

  const fetchOwners = usePaginatedFetchItems(prospectOwnerOptionsPath);
  const fetchSources = useFetchItems(sourceOptionsPath);
  const fetchReferrers = usePaginatedFetchItems(referrerOptionsPath, {
    remove_inactive: true,
  });

  const jobsDropdown = useMultiDropdown({
    fetchItems: fetchJobs,
    paginated: true,
    selectedItems: selectedJobs,
    onSelectedItemsChange: (changes) =>
      setSelectedJobs(changes.selectedItems ?? []),
    preventLoadOnMount: true,
  });

  const departmentsDropdown = useSingleDropdown({
    fetchItems: fetchDepartments,
    selectedItem: selectedDepartment,
    onSelectedItemChange: (e) => setSelectedDepartment(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const officesDropdown = useSingleDropdown({
    fetchItems: fetchOffices,
    paginated: true,
    selectedItem: selectedOffice,
    onSelectedItemChange: (e) => setSelectedOffice(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const poolsDropdown = useSingleDropdown({
    fetchItems: fetchPools,
    selectedItem: selectedPool,
    onSelectedItemChange: (e) => setSelectedPool(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const ownersDropdown = useSingleDropdown({
    fetchItems: fetchOwners,
    paginated: true,
    selectedItem: selectedOwner,
    onSelectedItemChange: (e) => setSelectedOwner(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const sourcesDropdown = useSingleDropdown({
    fetchItems: fetchSources,
    selectedItem: selectedSource,
    onSelectedItemChange: (e) => setSelectedSource(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const referrersDropdown = useSingleDropdown({
    fetchItems: fetchReferrers,
    paginated: true,
    selectedItem: selectedReferrer,
    onSelectedItemChange: (e) => setSelectedReferrer(e.selectedItem ?? null),
    preventLoadOnMount: true,
  });

  const onSubmit = async () => {
    setSubmitting(true);

    const formData = new FormData();

    selectedJobs.forEach((job) =>
      formData.append('prospective_hiring_plan_ids[]', job.value)
    );

    formData.append('referrer_application_id', applicationId);

    if (selectedDepartment) {
      formData.append('prospective_department_id', selectedDepartment.value);
    }
    if (selectedOffice) {
      formData.append('prospective_office_id', selectedOffice.value);
    }
    if (selectedPool) {
      formData.append('prospect_pool_id', selectedPool.value);
    }
    if (selectedStage) {
      formData.append('prospect_pool_stage_id', selectedStage.value);
    }
    if (selectedOwner) {
      formData.append('prospect_owner_id', selectedOwner.value);
    }
    if (selectedSource) {
      formData.append('source_id', selectedSource.value);
    }
    if (selectedReferrer) {
      formData.append('referred_by_id', selectedReferrer.value);
    }

    try {
      await standardRequestWithFlash.post(makeProspectPath, formData, {
        successMessage: t('success'),
      });
      onSuccess?.({
        ...Object.fromEntries(formData),
        'prospective_hiring_plan_ids[]': selectedJobs.map((job) => job.value),
      });
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title', { name: fullName })}
      size="medium"
    >
      <DataEntryModal.Header onClick={closeModal}>
        <span className="pii-blur">{t('title', { name: fullName })}</span>
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <FullWidth>
          <Stack spacing={3}>
            <MultiDropdown dropdownProps={jobsDropdown}>
              <MultiDropdown.Field
                label={t('job_name')}
                placeholder={t('job_name_placeholder')}
              />
              <MultiDropdown.Flyout />
            </MultiDropdown>

            <SingleDropdown dropdownProps={departmentsDropdown}>
              <SingleDropdown.Field
                label={t('department')}
                placeholder={t('department_placeholder')}
              />
              <SingleDropdown.Flyout />
            </SingleDropdown>

            <SingleDropdown
              key={`office-${officeKey}`}
              dropdownProps={officesDropdown}
            >
              <SingleDropdown.Field
                label={t('office')}
                disabled={!selectedDepartment}
              />
              <SingleDropdown.Flyout />
            </SingleDropdown>

            <SingleDropdown
              key={`pool-${selectedPool?.value}`}
              dropdownProps={poolsDropdown}
            >
              <SingleDropdown.Field
                label={t('pool')}
                placeholder={t('pool_placeholder')}
              />
              <SingleDropdown.Flyout />
            </SingleDropdown>

            <SimpleSingleDropdown
              key={`stage-${selectedPool?.value ?? 'none'}`}
              label={t('stage')}
              placeholder={t('stage_placeholder')}
              items={stageOptions}
              selectedItem={selectedStage}
              onSelectedItemChange={setSelectedStage}
              disabled={!selectedPool}
            />

            <div className="pii-blur">
              <SingleDropdown dropdownProps={ownersDropdown}>
                <SingleDropdown.Field
                  label={t('owner')}
                  placeholder={t('owner_placeholder')}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>
            </div>

            <div className="pii-blur">
              <SingleDropdown dropdownProps={sourcesDropdown}>
                <SingleDropdown.Field
                  label={t('source')}
                  placeholder={t('source_placeholder')}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>
            </div>

            <div className="pii-blur">
              <SingleDropdown dropdownProps={referrersDropdown}>
                <SingleDropdown.Field
                  label={t('referrer')}
                  placeholder={t('referrer_placeholder')}
                />
                <SingleDropdown.Flyout />
              </SingleDropdown>
            </div>
          </Stack>
        </FullWidth>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SecondaryButton onClick={closeModal} disabled={submitting}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton onClick={onSubmit} disabled={submitting}>
            {t('save')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default MakeProspectModal;
